import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import debounce from 'lodash/debounce';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { mapboxBoundsToSDKBounds } from '../../components/SearchMap/SearchMapWithMapbox.util';
import config from '../../config';
import { addBookmark, openBookmarkLoginRequiredModal, removeBookmark } from '../../ducks/bookmarks.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import routeConfiguration from '../../routeConfiguration';
import { hasCurrentUserValidAnnualPass } from '../../util/annualPass';
import useAppSelector from '../../util/hooks/useAppSelector';
import { isCordova } from '../../util/mobile/deviceDetection';
import { createResourceLocatorString } from '../../util/routes';
import { parse } from '../../util/urlHelpers';
import SearchPageContent from '../SearchPage/SearchPage.content';
import { setActiveListing } from '../SearchPage/SearchPage.duck';
import { validFilterParams, validSortParams, validURLParamsForExtendedData } from '../SearchPage/SearchPage.helpers';
import { SEARCH_WITH_MAP_DEBOUNCE } from '../SearchPage/SearchPage.util';

const query = '?bounds=59.68940654%2C19.07422688%2C30.17883172%2C-11.8484966';

const containerStyle = {
  px: 0,
  pt: 2,
  pb: { xs: 0, md: 6 },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
};

const SectionLandingResultsComponent = () => {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const {
    searchInProgress,
    searchListingsError,
    activeListingId,
    currentPageResultIds,
    pagination,
  } = useAppSelector((state) => state.SearchPage);
  const { currentBookmarks: stateBookmarks } = useAppSelector((state) => state.bookmarks);
  const { currentUser } = useAppSelector((state) => state.user);

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );
  const onActivateListing = useCallback(
    (listingId) => dispatch(setActiveListing(listingId)),
    [dispatch]
  );
  const onOpenBookmarkLoginRequiredModal = useCallback(
    () => dispatch(openBookmarkLoginRequiredModal()),
    [dispatch]
  );

  const entities = useAppSelector((state) => state.marketplaceData.entities);
  const listings = useMemo(
    () => getListingsById(entities, currentPageResultIds),
    [entities, currentPageResultIds]
  );

  const mapListings = listings;

  const [isSearchMapOpenOnMobile, setIsSearchMapOpenOnMobile] = useState(false);
  const [currentBookmarks, setCurrentBookmarks] = useState(stateBookmarks);
  const parsedQuery = parse(query, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const filterConfig = config.custom.filters;
  const sortConfig = config.custom.sortConfig;
  const onMapMoveEnd = debounce((viewportBoundsChanged, data) => {
    const { viewportBounds, viewportCenter } = data;

    //only update every kilometer if the currentLocation is followed

    const routes = routeConfiguration();

    // If mapSearch url param is given
    // or original location search is rendered once,
    // we start to react to "mapmoveend" events by generating new searches
    // (i.e. 'moveend' event in Mapbox and 'bounds_changed' in Google Maps)
    const hasSameBoundsCorners =
      viewportBounds &&
      viewportBounds.ne.lat === viewportBounds.sw.lat &&
      viewportBounds.ne.lng === viewportBounds.sw.lng;
    if (isCordova() && hasSameBoundsCorners) {
      return;
    }
    if (
      (viewportBoundsChanged && !window.followCurrentLocation) ||
      (viewportBoundsChanged && window.followCurrentLocation && window.updateSearchPage) ||
      (viewportBoundsChanged && window.followCurrentLocation && window.watchZoomChanged)
    ) {
      if (window.updateSearchPage) {
        window.updateSearchPage = false;
        window.watchFirstLocation = null;
      }
      if (window.watchZoomChanged) {
        window.watchZoomChanged = false;
      }

      // using window.location.search here to get the current query parameters
      const search =
        window.location.search ||
        new URLSearchParams(window.location.hash.split('?')[1] || '').toString();
      // parse query parameters, including a custom attribute named category
       
      const { mobileSearch, address, bounds, mapSearch, ...rest } = parse(search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });

      //const viewportMapCenter = SearchMap.getMapCenter(map);
      const originMaybe = config.sortSearchByDistance ? { origin: viewportCenter } : {};

      const searchParams = {
        address,
        ...originMaybe,
        bounds: viewportBounds,
        mapSearch: true,
        ...validFilterParams(rest, filterConfig),
        ...validSortParams(rest, sortConfig),
      };

      if (mobileSearch === 'open') return;
      navigate(
        createResourceLocatorString('SearchPage', routes, {}, searchParams, intl.locale)
      );
    }
  }, SEARCH_WITH_MAP_DEBOUNCE);
  const onOpenMobileModal = () => {
    setIsSearchMapOpenOnMobile(true);
  };
  const onCloseMobileModal = () => {
    setIsSearchMapOpenOnMobile(false);
  };
  const handleAddBookmark = async (listingId) => {
    if (!isAuthenticated) {
      onOpenBookmarkLoginRequiredModal();
    } else {
      const newBookmarks = await dispatch(addBookmark(listingId));
      setCurrentBookmarks(newBookmarks);
    }
  };
  const handleRemoveBookmark = async (listingId) => {
    if (!isAuthenticated) {
      onOpenBookmarkLoginRequiredModal();
    } else {
      const newBookmarks = await dispatch(removeBookmark(listingId));
      setCurrentBookmarks(newBookmarks);
    }
  };

  const { mapSearch, page, ...searchInURL } = parsedQuery;

  const searchParamsAreInSync = true;

  const validQueryParams = validURLParamsForExtendedData(searchInURL, filterConfig);

  const isWindowDefined = typeof window !== 'undefined';

  const onMapIconClick = () => {
    setIsSearchMapOpenOnMobile(true);
  };

  const onListIconClick = () => {
    setIsSearchMapOpenOnMobile(false);
  };

  let { bounds, origin } = searchInURL || {};

  if (isWindowDefined && window.autoZoomBounds) {
    bounds = mapboxBoundsToSDKBounds(window.autoZoomBounds);
  }

  const annualPassIsValid = hasCurrentUserValidAnnualPass(currentUser);

  return (
    <Container maxWidth="xl" sx={containerStyle}>
      <Typography mb={2} textAlign="center" variant="h3" width={1}>
        <FormattedMessage id="SectionLandingResults.header" />
      </Typography>
      <SearchPageContent
        activeListingId={activeListingId}
        annualPassIsValid={annualPassIsValid}
        bounds={bounds}
        currentBookmarks={currentBookmarks}
        disableHoverOnListings={true}
        handleAddBookmark={handleAddBookmark}
        handleRemoveBookmark={handleRemoveBookmark}
        isLandingPage
        isSearchMapOpenOnMobile={isSearchMapOpenOnMobile}
        listings={listings}
        location={location}
        mapListings={mapListings}
        onActivateListing={onActivateListing}
        onCloseMobileModal={onCloseMobileModal}
        onListIconClick={onListIconClick}
        onManageDisableScrolling={onManageDisableScrolling}
        onMapIconClick={onMapIconClick}
        onMapMoveEnd={onMapMoveEnd}
        onOpenMobileModal={onOpenMobileModal}
        origin={origin}
        pagination={pagination}
        searchInProgress={searchInProgress}
        searchListingsError={searchListingsError}
        searchParamsAreInSync={searchParamsAreInSync}
        searchParamsForPagination={{}}
        setIsSearchMapOpenOnMobile={setIsSearchMapOpenOnMobile}
        shouldShowSearchMap={isSearchMapOpenOnMobile}
        tab="listings"
        validQueryParams={validQueryParams}
      />
    </Container>
  );
};

SectionLandingResultsComponent.displayName = 'SectionLandingResults';
const SectionLandingResults = memo(SectionLandingResultsComponent);
export default SectionLandingResults;
